import { useAuthContext } from "@/context/auth-context";
import {
  EventSourceMessage,
  fetchEventSource as baseFetchEventSource,
} from "@microsoft/fetch-event-source";
import { useRef, useState } from "react";

const DEFAULT_ATTEMPTS = 1;

type Method = "GET" | "POST" | "PUT" | "DELETE";

export class EventSourceRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly payload: unknown,
  ) {
    super(message);
    this.name = "EventSourceRequestError";
  }
}

export class EventSourceMessageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EventSourceMessageError";
  }
}

type FetchEventSourceParams<T, B> = {
  method: Method;
  path: string;
  token: string | null;
  body?: B;
  attempts?: number;
  abortController?: AbortController;
  onMessage: (data: T) => void;
  onClose?: () => void;
  /** Handles both connection and message errors. */
  onError: (err: Error) => void | null;
  onNewAttempt?: (attempt: number) => void;
};

type UseEventSourceParams<B> = {
  method: Method;
  basePath: string;
  attempts?: number;
  body?: B;
};

type RequestParams<T, B> = {
  path?: string;
  body?: B;
  onMessage: (data: T) => void;
  onClose?: () => void;
  /** Handles both connection and message errors. */
  onError: (err: Error) => void | null;
};

/**
 * Naked wrapper to make SSE calls.
 *
 * @example
 * ```typescript
 * await fetchEventSource({
 *   method: "POST",
 *   path: "/api/path",
 *   token: userToken,
 *   attempts: 3, // optional, 1 is default
 *   body: {},
 *   onMessage: (data) => {},
 *   onError: (error) => {}
 * })
 * ```
 */
const fetchEventSource = async <T, B>({
  method,
  path,
  token,
  body,
  attempts = DEFAULT_ATTEMPTS,
  abortController,
  onMessage,
  onClose,
  onError,
  onNewAttempt,
}: FetchEventSourceParams<T, B>) => {
  let attemptNumber = 1;

  const headers: Record<string, string> = {
    "Content-Type": "text/event-stream",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (body) {
    headers["Content-Type"] = "application/json";
  }

  return await baseFetchEventSource(path, {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : undefined,
    openWhenHidden: true,
    signal: abortController?.signal,
    onopen: async (response) => {
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message =
          payload?.response?.message ?? `Request failed (${response.status})`;
        throw new EventSourceRequestError(message, response.status, payload);
      }
      onNewAttempt?.(1);
    },
    onmessage: (event: EventSourceMessage) => {
      if (event.event === "error") {
        const error = new EventSourceMessageError(event.data);
        onError(error);
        return;
      }

      if (!event.data || event.data === "") {
        return;
      }

      const data: T = JSON.parse(event.data);
      onMessage(data);
    },
    onerror: (error) => {
      if (attemptNumber === attempts) {
        onError(error);
        throw error; // Don't retry
      }

      attemptNumber++;
      onNewAttempt?.(attemptNumber);
    },
    onclose: () => {
      onClose?.();
    },
  });
};

/**
 * React hook to make SSE calls.
 *
 * @example
 * ```typescript
 * const { request, abort, error, attempt, attemptsRemaining, aborted, isConnected } = useEventSource({
 *   method: "POST",
 *   basePath: "/api/path",
 *   attempts: 3, // optional, 1 is default
 *   body: {},
 * });
 *
 * request({
 *   onMessage: (data) => {},
 *   onError: (error) => {},
 * })
 * ```
 */
export const useEventSource = <T = unknown, B = Record<string, unknown>>({
  method,
  basePath,
  attempts = DEFAULT_ATTEMPTS,
  body: hookBody,
}: UseEventSourceParams<B>) => {
  const authContext = useAuthContext();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [aborted, setAborted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const request = async ({
    body: requestBody,
    path,
    onMessage,
    onClose,
    onError,
  }: RequestParams<T, B>): Promise<void> => {
    const body = requestBody || hookBody;
    abortControllerRef.current = new AbortController();

    return await fetchEventSource({
      path: basePath + (path || ""),
      method,
      body,
      token: authContext.token ?? null,
      attempts,
      abortController: abortControllerRef.current,
      onNewAttempt: (attemptNumber) => {
        setAttempt(attemptNumber);
      },
      onMessage,
      onClose: () => {
        setIsConnected(false);
        onClose?.();
      },
      onError: (error) => {
        onError(error);
        setError(error);
      },
    });
  };

  return {
    request,
    abort: () => {
      abortControllerRef.current?.abort();
      setAborted(true);
    },
    error,
    attempt,
    attemptsRemaining: attempts + 1 - attempt,
    aborted,
    isConnected,
  };
};
